import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MdCall } from "react-icons/md";
import axios from "axios";

const WaitingList = ({user}) => {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [waitingList, setWaitingList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // Minimum 5 items per page

  useEffect(() => {
    const fetchWaitingList = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER_API}/api/waiting-list?userId=${user.id}&role=${user.role}`);
        setWaitingList(response.data.map((item, index) => ({
          srNo: index + 1,
          ...item,
        })));
      } catch (err) {
        setError("Failed to fetch waiting list. Please try again.");
        console.error("Error fetching waiting list:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchWaitingList();
  }, []);

  const exportToCSV = () => {
    console.log("Exporting to CSV...");
    // Add CSV export logic here if needed
  };

  const exportToXLSX = () => {
    console.log("Exporting to XLSX...");
    // Add XLSX export logic here if needed
  };

  // Filter data based on search input
  const filteredData = waitingList.filter((row) =>
    row.to.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="flex justify-between items-center mb-6"
      >
        <h1 className="text-3xl font-bold text-gray-900">Waiting List</h1>
        <div className="space-x-4">
          <button
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 focus:outline-none"
            onClick={exportToCSV}
          >
            Export to CSV
          </button>
          <button
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none"
            onClick={exportToXLSX}
          >
            Export to XLSX
          </button>
        </div>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="mb-6"
      >
        <input
          type="text"
          placeholder="Search by Contact Name"
          className="w-full md:w-1/4 px-4 py-3 border border-gray-300 rounded-xl shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 ease-in-out transform focus:scale-105"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </motion.div>

      {/* Loading and Error States */}
      {loading && (
        <motion.div
          className="flex justify-center items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="animate-spin w-8 h-8 border-4 border-t-4 border-blue-500 rounded-full"></div>
        </motion.div>
      )}
      {error && (
        <motion.p
          className="text-red-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {error}
        </motion.p>
      )}

      {/* Dynamic Table */}
      {!loading && !error && (
        <>
          <motion.div
            className="overflow-x-auto bg-white shadow-lg rounded-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
              <thead className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-700 text-white">
                <tr>
                  <th className="px-6 py-3 text-left">Sr No</th>
                  <th className="px-6 py-3 text-left">Agent Name</th>
                  <th className="px-6 py-3 text-left">Contact Name</th>
                  <th className="px-6 py-3 text-left">Status</th>
                  <th className="px-6 py-3 text-left">Timestamp</th>
                  <th className="px-6 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row) => (
                    <tr key={row.callSid} className="border-t">
                      <td className="px-6 py-4">{row.srNo}</td>
                      <td className="px-6 py-4">{row.from}</td>
                      <td className="px-6 py-4">{row.to}</td>
                      <td className="px-6 py-4">{row.status}</td>
                      <td className="px-6 py-4">
                        {new Date(row.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <button className="text-blue-500 hover:text-blue-700">
                          <MdCall size={24} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      No waiting list entries found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </motion.div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`px-4 py-2 rounded-lg ${
                    currentPage === index + 1
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WaitingList;